import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import {
  SalesRequestResultDTO,
  SalesRequestStatus,
} from '@app/api/purchase-service-sales';
import { PermissionService } from '@app/core/services/permission.service';
import {
  DISTRIBUTION_PAGE_SIZE,
  SALES_REQUEST_STATUSES,
  salesRequestSeverity,
} from '../distribution.constants';
import { SalesRequestService } from './services/sales-request.service';

@Component({
  selector: 'app-sales-request',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ButtonModule,
    ConfirmDialogModule,
    InputTextModule,
    SelectModule,
    TableModule,
    TagModule,
    ToastModule,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './sales-request.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SalesRequestComponent {
  private readonly router = inject(Router);
  private readonly service = inject(SalesRequestService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly translate = inject(TranslateService);
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    { initialValue: this.permissionService.getCurrentPermissions() },
  );

  readonly canCreate = computed(() => this.can('create'));
  readonly canApprove = computed(() => this.can('approve'));
  readonly canDelete = computed(() => this.can('delete'));

  readonly statusSeverity = salesRequestSeverity;
  readonly statusOptions = SALES_REQUEST_STATUSES.map((value) => ({
    value,
    label: `distribution.status.request.${value}`,
  }));

  readonly rows = signal<SalesRequestResultDTO[]>([]);
  readonly total = signal(0);
  readonly loading = signal(false);
  readonly first = signal(0);
  readonly pageSize = DISTRIBUTION_PAGE_SIZE;

  series = '';
  title = '';
  status: SalesRequestStatus | null = null;

  private can(action: string): boolean {
    this.permissionCodes();
    return this.permissionService.canDoAction(
      `distribution.sales_request.${action}`,
    );
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    this.first.set(event.first ?? 0);
    this.load(event.rows ?? this.pageSize);
  }

  onSearch(): void {
    this.first.set(0);
    this.load(this.pageSize);
  }

  onReset(): void {
    this.series = '';
    this.title = '';
    this.status = null;
    this.onSearch();
  }

  onAdd(): void {
    this.router.navigate(['/distribution/sales-request/add']);
  }

  onView(row: SalesRequestResultDTO): void {
    this.router.navigate(['/distribution/sales-request/view', row.id]);
  }

  onApprove(row: SalesRequestResultDTO): void {
    this.confirmationService.confirm({
      message: this.translate.instant(
        'distribution.sales_request.confirm_approve',
        { series: row.series },
      ),
      accept: () =>
        this.run(this.service.approve(row.id!), 'distribution.messages.approved'),
    });
  }

  onDelete(row: SalesRequestResultDTO): void {
    this.confirmationService.confirm({
      message: this.translate.instant(
        'distribution.sales_request.confirm_delete',
        { series: row.series },
      ),
      accept: () =>
        this.run(this.service.delete(row.id!), 'distribution.messages.deleted'),
    });
  }

  private run(
    request$: ReturnType<SalesRequestService['approve']>,
    successKey: string,
  ): void {
    request$.subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.messages.success'),
          detail: this.translate.instant(successKey),
        });
        this.load(this.pageSize);
      },
      error: (err) => this.showError(err),
    });
  }

  private load(size: number): void {
    this.loading.set(true);
    this.service
      .search({
        series: this.series,
        title: this.title,
        status: this.status,
        first: this.first(),
        rows: size,
      })
      .subscribe({
        next: (page) => {
          this.rows.set(page.pageContent ?? []);
          this.total.set(page.totalElements ?? 0);
          this.loading.set(false);
        },
        error: (err) => {
          this.loading.set(false);
          this.showError(err);
        },
      });
  }

  private showError(err: unknown): void {
    const detail =
      (err as { error?: { message?: string } })?.error?.message ??
      this.translate.instant('distribution.messages.failed');
    this.messageService.add({
      severity: 'error',
      summary: this.translate.instant('common.messages.error'),
      detail,
    });
  }
}
